var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React from 'react';
import PropTypes from 'prop-types';
import sizeMe from 'react-sizeme';
import Scaler from '../Scaler';
import ScalerContext from '../../context/Scaler';
import PointCollection from '../PointCollection';
import InteractionLayer, { ZoomMode } from '../InteractionLayer';
import GriffPropTypes, { seriesPropType } from '../../utils/proptypes';
import XAxis from '../XAxis';
import Layout from './Layout';
import AxisPlacement from '../AxisPlacement';
import GridLines from '../GridLines';
import Axes from '../../utils/Axes';
import AxisCollection from '../AxisCollection';
import LineCollection from '../LineCollection';
import AxisDisplayMode from '../LineChart/AxisDisplayMode';

var propTypes = {
  grid: GriffPropTypes.grid,
  size: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  zoomable: PropTypes.bool,
  onClick: PropTypes.func,
  // Number => String
  xAxisFormatter: PropTypes.func,
  xAxisPlacement: GriffPropTypes.axisPlacement,
  xAxisTicks: PropTypes.number,
  // Number => String
  yAxisFormatter: PropTypes.func,
  yAxisPlacement: GriffPropTypes.axisPlacement,
  yAxisTicks: PropTypes.number,
  collections: GriffPropTypes.collections.isRequired,
  series: seriesPropType.isRequired
};

var defaultProps = {
  grid: null,
  zoomable: true,
  onClick: null,
  xAxisFormatter: Number,
  xAxisPlacement: AxisPlacement.BOTTOM,
  xAxisTicks: null,
  yAxisFormatter: Number,
  yAxisPlacement: AxisPlacement.RIGHT,
  yAxisTicks: null
};

var Y_AXIS_WIDTH = 50;
var X_AXIS_HEIGHT = 50;

var getYAxisPlacement = function getYAxisPlacement(_ref) {
  var collections = _ref.collections,
      series = _ref.series,
      yAxisPlacement = _ref.yAxisPlacement;

  var yAxisPlacements = [].concat(series.filter(function (s) {
    return s.collectionId === undefined;
  })).concat(collections).reduce(function (acc, item) {
    var placement = item.yAxisPlacement || yAxisPlacement;
    if (placement) {
      acc[placement] = (acc[placement] || 0) + 1;
    }
    return acc;
  }, {});
  if (yAxisPlacements[AxisPlacement.BOTH]) {
    return AxisPlacement.BOTH;
  }
  if (yAxisPlacements[AxisPlacement.LEFT] && yAxisPlacements[AxisPlacement.RIGHT]) {
    return AxisPlacement.BOTH;
  }
  if (yAxisPlacements[AxisPlacement.LEFT]) {
    return AxisPlacement.LEFT;
  }
  return yAxisPlacement || AxisPlacement.RIGHT;
};

var ScatterplotComponent = function ScatterplotComponent(_ref2) {
  var collections = _ref2.collections,
      grid = _ref2.grid,
      series = _ref2.series,
      _ref2$size = _ref2.size,
      width = _ref2$size.width,
      height = _ref2$size.height,
      zoomable = _ref2.zoomable,
      onClick = _ref2.onClick,
      xAxisFormatter = _ref2.xAxisFormatter,
      xAxisPlacement = _ref2.xAxisPlacement,
      xAxisTicks = _ref2.xAxisTicks,
      yAxisFormatter = _ref2.yAxisFormatter,
      propsYAxisPlacement = _ref2.yAxisPlacement,
      yAxisTicks = _ref2.yAxisTicks;

  var chartSize = {
    width: width,
    height: height
  };

  var collectionVisibility = collections.reduce(function (acc, c) {
    var _extends2;

    return _extends({}, acc, (_extends2 = {}, _extends2[c.id] = !c.hidden && c.yAxisDisplayMode !== AxisDisplayMode.NONE, _extends2));
  }, {});

  var seriesVisibility = series.reduce(function (acc, s) {
    var _extends3;

    return _extends({}, acc, (_extends3 = {}, _extends3[s.id] =
    // If it's hidden, it won't have an axis.
    !s.hidden &&
    // If it has a non-hidden axis, it will not have an axis.
    s.yAxisDisplayMode !== AxisDisplayMode.NONE && (
    // If it's in a collection, it gets special behavior ...
    s.collectionId &&
    // If it's in an unknown collection, it will have an axis.
    collectionVisibility[s.collectionId] === undefined ||
    // And if it's not in a collection, it gets its own axis
    s.collectionId === undefined), _extends3));
  }, {});

  var visibleAxes = Object.values(seriesVisibility).concat(Object.values(collectionVisibility)).filter(Boolean).length;

  var yAxisPlacement = getYAxisPlacement({
    collections: collections,
    series: series,
    propsYAxisPlacement: propsYAxisPlacement
  });

  chartSize.width -= visibleAxes * Y_AXIS_WIDTH;

  switch (xAxisPlacement) {
    case AxisPlacement.BOTH:
      chartSize.height -= 2 * X_AXIS_HEIGHT;
      break;
    default:
      chartSize.height -= X_AXIS_HEIGHT;
      break;
  }

  return React.createElement(Layout, {
    chart: React.createElement(
      'svg',
      { style: { width: '100%', height: '100%' } },
      React.createElement(GridLines, _extends({ grid: grid }, chartSize)),
      React.createElement(PointCollection, chartSize),
      React.createElement(LineCollection, _extends({}, chartSize, {
        series: series.filter(function (s) {
          return !!s.drawLines;
        }),
        xAxis: Axes.x
      })),
      React.createElement(InteractionLayer, _extends({}, chartSize, {
        onClick: onClick,
        zoomMode: ZoomMode.BOTH,
        zoomAxes: { x: zoomable, y: zoomable }
      }))
    ),
    yAxis: React.createElement(AxisCollection, {
      tickFormatter: yAxisFormatter,
      yAxisPlacement: yAxisPlacement,
      height: chartSize.height,
      width: Y_AXIS_WIDTH,
      ticks: yAxisTicks
    }),
    xAxis: React.createElement(XAxis, {
      width: chartSize.width,
      height: X_AXIS_HEIGHT,
      tickFormatter: xAxisFormatter,
      ticks: xAxisTicks,
      axis: Axes.x
    }),
    xAxisPlacement: xAxisPlacement,
    yAxisPlacement: yAxisPlacement
  });
};

ScatterplotComponent.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
ScatterplotComponent.defaultProps = defaultProps;

var SizedScatterplotComponent = sizeMe({
  monitorHeight: true
})(ScatterplotComponent);

var Scatterplot = function Scatterplot(props) {
  return React.createElement(
    Scaler,
    null,
    React.createElement(
      ScalerContext.Consumer,
      null,
      function (_ref3) {
        var collections = _ref3.collections,
            series = _ref3.series;
        return React.createElement(SizedScatterplotComponent, _extends({}, props, {
          collections: collections,
          series: series
        }));
      }
    )
  );
};

export default Scatterplot;